import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/common/stores/authStore';
import { NotificationCenter } from '@/modules/notifications';
import './Header.css';

interface NavItem {
  label: string;
  href?: string;
  submenu?: Array<{
    label: string;
    href: string;
    description?: string;
  }>;
}

const Header: React.FC = () => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const user = useAuthStore((state) => state.user);
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const handleLogout = async () => {
    await logout();
    void navigate('/');
  };


  const navItems: NavItem[] = [];

  return (
    <header className="header">
      <div className="header__container">
        {/* Logo */}
        <RouterLink
          to={isAuthenticated ? "/home" : "/"}
          className={`header__logo ${isAuthenticated ? 'header__logo--authenticated' : ''}`}
        >
          <span className="header__logo-text">EvenSteven</span>
        </RouterLink>

        {/* Navigation - Desktop */}
        <nav className="header__nav">
          {/* Menu Left - Navigation Items */}
          <div className="header__nav-left">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="header__nav-item"
                onMouseEnter={() => setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {item.submenu ? (
                  <>
                    <button className="header__nav-button">
                      {item.label}
                      <span className="header__nav-arrow">▼</span>
                    </button>

                    {/* Dropdown Menu */}
                    <div
                      className={`header__dropdown ${
                        activeDropdown === item.label ? 'header__dropdown--active' : ''
                      }`}
                    >
                      {item.submenu.map((subitem) => (
                        <RouterLink
                          key={subitem.label}
                          to={subitem.href}
                          className="header__dropdown-item"
                        >
                          <div className="header__dropdown-label">{subitem.label}</div>
                          {subitem.description && (
                            <div className="header__dropdown-description">{subitem.description}</div>
                          )}
                        </RouterLink>
                      ))}
                    </div>
                  </>
                ) : (
                  <RouterLink to={item.href || '#'} className="header__nav-link">
                    {item.label}
                  </RouterLink>
                )}
              </div>
            ))}
          </div>

          {/* Menu Right - Auth Links or Search + User Menu */}
          <div className="header__nav-right">
            {user ? (
              <>
                {/* Search Store */}
                <div className="header__search-wrapper">
                  <input
                    type="text"
                    placeholder="Search stores..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="header__search-input"
                  />
                  <span className="header__search-icon">🔍</span>
                </div>

                {/* Notifications */}
                <NotificationCenter />

                {/* User Dropdown */}
                <div
                  className="header__nav-item"
                  onMouseEnter={() => setActiveDropdown('user')}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="header__user-button">
                    <span className="header__user-avatar">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </span>
                    <span className="header__user-name">{user.name}</span>
                    <span className="header__nav-arrow">▼</span>
                  </button>

                  {/* User Dropdown Menu */}
                  <div
                    className={`header__dropdown ${
                      activeDropdown === 'user' ? 'header__dropdown--active' : ''
                    }`}
                  >
                    <RouterLink to="/profile" className="header__dropdown-item">
                      <div className="header__dropdown-label">Profile</div>
                    </RouterLink>
                    <RouterLink to="/settings" className="header__dropdown-item">
                      <div className="header__dropdown-label">Settings</div>
                    </RouterLink>
                    <button
                      onClick={handleLogout}
                      className="header__dropdown-item header__dropdown-logout"
                    >
                      <div className="header__dropdown-label">Log out</div>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="header__button-wrapper">
                <RouterLink to="/login" className="header__auth-link header__auth-link--login">
                  Log in
                </RouterLink>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className={`header__mobile-toggle ${mobileMenuOpen ? 'header__mobile-toggle--active' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav className="header__mobile-menu">
          {navItems.map((item) => (
            <div key={item.label} className="header__mobile-item">
              {item.submenu ? (
                <>
                  <div className="header__mobile-label">{item.label}</div>
                  <div className="header__mobile-submenu">
                    {item.submenu.map((subitem) => (
                      <RouterLink
                        key={subitem.label}
                        to={subitem.href}
                        className="header__mobile-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {subitem.label}
                      </RouterLink>
                    ))}
                  </div>
                </>
              ) : (
                <RouterLink
                  to={item.href || '#'}
                  className="header__mobile-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </RouterLink>
              )}
            </div>
          ))}
          <div className="header__mobile-auth">
            <RouterLink
              to="/login"
              className="header__mobile-auth-link header__mobile-auth-link--login"
              onClick={() => setMobileMenuOpen(false)}
            >
              Log in
            </RouterLink>
          </div>
        </nav>
      )}

    </header>
  );
};

export default Header;
